import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import type {
  Task,
  User,
  Tag,
  TaskUpdateDto,
  RoomStatus,
  Reminder,
} from "../types/types.ts";
import { addDays, format, eachDayOfInterval } from "date-fns";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Switch from "@mui/material/Switch";
import { isoToYmd } from "../utils/date.ts";
import { TaskDetailPanel } from "./TaskDetailPanel.tsx";
import {
  PaletteColor,
  PaletteColorBg,
  PaletteColorHex,
  PaletteColorDarkBg,
  PaletteColorDarkHex,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

const getJstYmd = (isoStr: string | null | undefined): string => {
  if (!isoStr) return "";
  try {
    return format(new Date(isoStr), "yyyy-MM-dd");
  } catch {
    return "";
  }
};

interface TimelineViewProps {
  tasks: Task[];
  roomMembers: User[];
  onUpdateTask: (taskId: string, updates: TaskUpdateDto) => Promise<void>;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  onDeleteTask: (taskId: string) => Promise<void>;
  availableTags: Tag[];
  onManageTags: () => void;
  color: PaletteColor;
  addReminder: (
    taskId: string,
    reminderDateTime: Date
  ) => Promise<Reminder | null>;
  deleteReminder: (reminderId: string | number) => Promise<boolean>;
  roomStatuses: RoomStatus[];
  forceReload: () => void;
  honbuId: number;
  roomId: number;
  activeListId: string | null;
  onOpenRecurrenceManagement?: (
    selectedTaskId?: string,
    selectedRuleId?: number,
  ) => void;
}

const LABEL_COL_WIDTH = 160;
const DAY_COL_BASE_WIDTH = 150;

const TaskBar = ({
  task,
  showTitle,
  color,
  onShowDetail,
  roomStatuses,
  isDarkMode,
}: {
  task: Task & { isSubtask?: boolean };
  showTitle: boolean;
  color: PaletteColor;
  onShowDetail: (task: Task) => void;
  roomStatuses: RoomStatus[];
  isDarkMode: boolean;
}) => {
  const statusInfo = roomStatuses.find((s) => s.statusName === task.status);
  const barBg = statusInfo
    ? isDarkMode
      ? PaletteColorDarkBg[statusInfo.color]
      : PaletteColorBg[statusInfo.color]
    : isDarkMode
    ? "#374151"
    : "#F3F4F6";
  const barBorder = statusInfo
    ? isDarkMode
      ? PaletteColorDarkHex[statusInfo.color]
      : PaletteColorHex[statusInfo.color]
    : "#8A94A6";

  return (
    <div
      onClick={() => onShowDetail(task)}
      className="h-6 px-2 flex items-center rounded cursor-pointer group overflow-hidden"
      style={{
        backgroundColor: barBg,
        borderLeft: `3px solid ${barBorder}`,
      }}
      title={task.title}
    >
      {showTitle && (
        <div className="flex items-center gap-1.5 min-w-0">
          {task.isSubtask && (
            <div
              className={`flex-shrink-0 w-6 h-[14px] text-[10px] leading-none font-bold rounded-sm flex items-center justify-center ${
                isDarkMode
                  ? "text-gray-300 bg-gray-600"
                  : "text-gray-600 bg-gray-200"
              }`}
            >
              Sub
            </div>
          )}
          <span
            className={`text-xs font-medium truncate ${
              isDarkMode ? "text-gray-200" : "text-gray-800"
            }`}
          >
            {task.title}
          </span>
        </div>
      )}
    </div>
  );
};

const useResizableColumns = (baseColWidth: number) => {
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const resizingRef = useRef<{
    key: string;
    startPos: number;
    startSize: number;
  } | null>(null);
  const getColWidth = useCallback(
    (key: string) => colWidths[key] || baseColWidth,
    [colWidths, baseColWidth]
  );

  const handleColMouseDown = useCallback(
    (e: React.MouseEvent, key: string) => {
      e.preventDefault();
      resizingRef.current = {
        key,
        startPos: e.clientX,
        startSize: getColWidth(key),
      };
      document.body.style.cursor = "col-resize";
    },
    [getColWidth]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const { key, startPos, startSize } = resizingRef.current;
      const newWidth = startSize + (e.clientX - startPos);
      if (newWidth > 60) setColWidths((prev) => ({ ...prev, [key]: newWidth }));
    };
    const handleMouseUp = () => {
      resizingRef.current = null;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return { getColWidth, handleColMouseDown };
};

export function TimelineView({
  tasks,
  roomMembers,
  onUpdateTask,
  onUpdateStatus,
  onDeleteTask,
  availableTags,
  onManageTags,
  color,
  addReminder,
  deleteReminder,
  roomStatuses,
  forceReload,
  honbuId,
  roomId,
  activeListId,
  onOpenRecurrenceManagement,
}: TimelineViewProps) {
  const isDarkMode = useDarkMode();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [numberOfDays, setNumberOfDays] = useState(7);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState({ top: 0, left: 0 });

  const [detailTask, setDetailTask] = useState<Task | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const optionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const optionsMenuRef = useRef<HTMLDivElement | null>(null);

  const storageKey = `timeline_settings_${honbuId}_${roomId}_${
    activeListId || "none"
  }`;
  const [showNoDue, setShowNoDue] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [visibleUserIds, setVisibleUserIds] = useState<Set<string>>(new Set());

  // ドラッグ&ドロップ用の状態
  const [userOrder, setUserOrder] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // ★ 全タスク（親＋子タスク）を展開
  const allTasks = useMemo(() => {
    const result: (Task & { isSubtask?: boolean; parentId?: string })[] = [];
    tasks.forEach((task) => {
      result.push(task);
      if (task.childTasks && task.childTasks.length > 0) {
        task.childTasks.forEach((child) => {
          result.push({ ...child, isSubtask: true, parentId: task.id });
        });
      }
    });
    return result;
  }, [tasks]);

  // ★ storageKeyが変更されたら、LocalStorageから設定を再読み込み
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setShowNoDue(parsed.showNoDue ?? true);
        setShowUnassigned(parsed.showUnassigned ?? true);
        if (parsed.visibleUserIds && Array.isArray(parsed.visibleUserIds)) {
          setVisibleUserIds(new Set(parsed.visibleUserIds));
        } else {
          setVisibleUserIds(new Set(roomMembers.map((m) => m.id)));
        }
        if (parsed.userOrder && Array.isArray(parsed.userOrder)) {
          setUserOrder(parsed.userOrder);
        } else {
          setUserOrder(roomMembers.map((m) => m.id));
        }
      } catch {
        setShowNoDue(true);
        setShowUnassigned(true);
        setVisibleUserIds(new Set(roomMembers.map((m) => m.id)));
        setUserOrder(roomMembers.map((m) => m.id));
      }
    } else {
      setShowNoDue(true);
      setShowUnassigned(true);
      setVisibleUserIds(new Set(roomMembers.map((m) => m.id)));
      setUserOrder(roomMembers.map((m) => m.id));
    }
  }, [storageKey, roomMembers]);

  // roomMembersが変更されたら、新しいユーザーを自動的に表示状態にする
  useEffect(() => {
    setVisibleUserIds((prev) => {
      const newSet = new Set(prev);
      roomMembers.forEach((m) => {
        if (!newSet.has(m.id)) {
          newSet.add(m.id);
        }
      });
      return newSet;
    });

    // 新しいメンバーを順序に追加
    setUserOrder((prev) => {
      const newOrder = [...prev];
      roomMembers.forEach((m) => {
        if (!newOrder.includes(m.id)) {
          newOrder.push(m.id);
        }
      });
      // 存在しないIDを削除
      return newOrder.filter((id) => roomMembers.some((m) => m.id === id));
    });
  }, [roomMembers]);

  // 設定が変更されたらLocalStorageに保存
  useEffect(() => {
    const settings = {
      showNoDue,
      showUnassigned,
      visibleUserIds: Array.from(visibleUserIds),
      userOrder,
    };
    localStorage.setItem(storageKey, JSON.stringify(settings));
  }, [showNoDue, showUnassigned, visibleUserIds, userOrder, storageKey]);

  // ドラッグ開始
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  // ドラッグオーバー
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  // ドロップ
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const newOrder = [...userOrder];
    const [draggedId] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedId);

    setUserOrder(newOrder);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // ドラッグ終了
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // ソート済みのroomMembers
  const orderedMembers = useMemo(() => {
    const memberMap = new Map(roomMembers.map((m) => [m.id, m]));
    return userOrder
      .map((id) => memberMap.get(id))
      .filter((m): m is User => m !== undefined);
  }, [roomMembers, userOrder]);

  const handleUpdateStatus = async (taskId: string, status: Task["status"]) => {
    try {
      await onUpdateStatus(taskId, status);
      await new Promise((resolve) => setTimeout(resolve, 100));
      forceReload();
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  useEffect(() => {
    if (detailTask) {
      const updatedTask = allTasks.find((t) => t.id === detailTask.id);
      if (updatedTask) {
        if (JSON.stringify(updatedTask) !== JSON.stringify(detailTask)) {
          setDetailTask(updatedTask);
        }
      } else {
        setDetailTask(null);
      }
    }
  }, [allTasks]);

  const dateRange = useMemo(
    () => ({ start: currentDate, end: addDays(currentDate, numberOfDays - 1) }),
    [currentDate, numberOfDays]
  );
  const timelineDays = useMemo(() => eachDayOfInterval(dateRange), [dateRange]);
  const timelineDayKeys = useMemo(
    () => timelineDays.map((d) => isoToYmd(d.toISOString())),
    [timelineDays]
  );

  const NO_DUE_DATE_COL_KEY = "__noDueDate__";
  const { getColWidth, handleColMouseDown } =
    useResizableColumns(DAY_COL_BASE_WIDTH);

  const timelineLayout = useMemo(() => {
    const timelineStartKey = timelineDayKeys[0];
    const timelineEndKey = timelineDayKeys[timelineDayKeys.length - 1];

    const getLayoutForUser = (
      userTasks: (Task & { isSubtask?: boolean })[]
    ) => {
      const lanes: (string | undefined)[][] = [];
      const renderableTasks: any[] = [];
      // -- ソート処理
      const tasksWithLayoutInfo = userTasks
        .map((task) => {
          let startKey: string;
          let endKey: string;

          if (task.completed && task.completedAt) {
            startKey = getJstYmd(task.completedAt);
            endKey = startKey;
          } else {
            startKey = getJstYmd(task.startDate);
            endKey = getJstYmd(task.endDate || task.startDate);
          }

          if (
            !startKey ||
            endKey < timelineStartKey ||
            startKey > timelineEndKey
          ) {
            return null;
          }

          const actualStartKey =
            startKey < timelineStartKey ? timelineStartKey : startKey;
          const actualEndKey =
            endKey > timelineEndKey ? timelineEndKey : endKey;

          const startIndex = timelineDayKeys.findIndex(
            (d) => d >= actualStartKey
          );
          let endIndex = timelineDayKeys.findIndex((d) => d >= actualEndKey);
          if (endIndex === -1) endIndex = timelineDayKeys.length - 1;

          const span = endIndex - startIndex + 1;

          return {
            task,
            startIndex,
            endIndex,
            span: span > 0 ? span : 1,
          };
        })
        .filter((t): t is NonNullable<typeof t> => t !== null);
      // -- ソート処理
      const sortedTasks = tasksWithLayoutInfo.sort((a, b) => {
        if (a.span !== b.span) {
          return b.span - a.span;
        }
        return a.startIndex - b.startIndex;
      });

      // -- 配置処理
      sortedTasks.forEach((taskInfo) => {
        const { task, startIndex, endIndex, span } = taskInfo;

        let laneIndex = lanes.findIndex((lane) => {
          for (let i = startIndex; i <= endIndex; i++) {
            if (lane[i] !== undefined) {
              return false;
            }
          }
          return true;
        });

        if (laneIndex === -1) {
          laneIndex = lanes.length;
          lanes.push([]);
        } // レーンに使用済みマークを付ける

        for (let i = startIndex; i <= endIndex; i++) {
          lanes[laneIndex][i] = task.id;
        } // レンダリング用配列に追加

        renderableTasks.push({
          task,
          lane: laneIndex,
          start: startIndex + 1,
          span: span,
          showTitle: true,
        });
      });
      const noDueDateTasks = userTasks.filter(
        (t) =>
          (!t.completed && !getJstYmd(t.startDate)) || // 未完了でstartDateなし
          (t.completed && !getJstYmd(t.completedAt)) // 完了でcompletedAtなし
      );

      return {
        tasks: renderableTasks,
        laneCount: lanes.length,
        noDueDateTasks: noDueDateTasks,
      };
    };

    const gridByUser: {
      [userId: string]: ReturnType<typeof getLayoutForUser>;
    } = {};
    roomMembers.forEach((member) => {
      const userTasks = allTasks.filter((t) => {
        if (t.completed && t.completedBy) {
          return t.completedBy.id === member.id;
        } else {
          return (t.assignees || []).some((a) => a.id === member.id);
        }
      });
      gridByUser[member.id] = getLayoutForUser(userTasks);
    });

    const unassignedTasks = allTasks.filter((t) => {
      if (t.completed) {
        return !t.completedBy;
      } else {
        return (t.assignees || []).length === 0;
      }
    });
    const unassignedLayout = getLayoutForUser(unassignedTasks);

    return { gridByUser, unassignedLayout };
  }, [allTasks, roomMembers, timelineDayKeys]);

  const shiftDays = (d: number) => setCurrentDate((p) => addDays(p, d));
  const handleToday = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  const todayKey = isoToYmd(new Date().toISOString());

  const handleOptionsClick = () => {
    if (optionsButtonRef.current && rootRef.current) {
      const buttonRect = optionsButtonRef.current.getBoundingClientRect();
      const containerRect = rootRef.current.getBoundingClientRect();

      setOptionsPosition({
        top: buttonRect.bottom - containerRect.top,
        left: buttonRect.left - containerRect.left,
      });
    }
    setOptionsOpen((o) => !o);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (
        optionsMenuRef.current &&
        !optionsMenuRef.current.contains(e.target as Node) &&
        optionsButtonRef.current &&
        !optionsButtonRef.current.contains(e.target as Node)
      ) {
        setOptionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div
      ref={rootRef}
      className={`relative flex flex-col overflow-hidden rounded-lg shadow p-4 ${
        isDarkMode ? "bg-gray-900" : "bg-white"
      }`}
      style={{ height: "calc(100vh - 4.5rem)" }}
    >
      <div className="flex items-center justify-end mb-3 flex-wrap gap-3 shrink-0">
        <div className="flex items-center flex-wrap gap-3">
          <span className={`font-bold text-lg mr-4 ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>
            {format(dateRange.start, "yyyy年M月d日")} -{" "}
            {format(dateRange.end, "M月d日")}
          </span>
          <div className="flex items-center gap-1 mr-2">
            <button
              onClick={() => shiftDays(-7)}
              aria-label="前の週"
              className={`w-8 h-8 rounded-md flex items-center justify-center ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              <DoubleArrowIcon
                className="!w-5 !h-5 rotate-180"
                fontSize="inherit"
              />
            </button>
            <button
              onClick={() => shiftDays(-1)}
              aria-label="前の日"
              className={`w-8 h-8 rounded-md flex items-center justify-center ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              <PlayArrowIcon
                className="!w-5 !h-5 rotate-180"
                fontSize="inherit"
              />
            </button>
            <button
              onClick={handleToday}
              className={`h-8 px-3 rounded-md text-sm font-semibold ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              Today
            </button>
            <button
              onClick={() => shiftDays(1)}
              aria-label="次の日"
              className={`w-8 h-8 rounded-md flex items-center justify-center ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              <PlayArrowIcon className="!w-5 !h-5" fontSize="inherit" />
            </button>
            <button
              onClick={() => shiftDays(7)}
              aria-label="次の週"
              className={`w-8 h-8 rounded-md flex items-center justify-center ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              <DoubleArrowIcon className="!w-5 !h-5" fontSize="inherit" />
            </button>
          </div>
        </div>
      </div>

      <div className={`flex-grow min-h-0 overflow-auto border rounded ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}>
        <div
          className="grid"
          style={{
            gridTemplateColumns: `${LABEL_COL_WIDTH}px ${
              showNoDue ? getColWidth(NO_DUE_DATE_COL_KEY) + "px " : ""
            }${timelineDayKeys
              .map((key) => getColWidth(key) + "px")
              .join(" ")}`,
          }}
        >
          {/* ヘッダー行 */}
          <div className={`sticky left-0 top-0 z-40 p-2 border-b border-r font-semibold text-sm flex items-center justify-between ${isDarkMode ? "bg-gray-800 text-gray-200 border-gray-600" : "bg-blue-100 text-gray-800"}`}>
            <button
              ref={optionsButtonRef}
              onClick={handleOptionsClick}
              aria-label="表示オプション"
              title="表示オプション"
              className={`px-1.5 py-0.5 rounded text-xs font-normal border transition ${
                isDarkMode
                  ? `bg-gray-700 hover:bg-gray-600 text-gray-200 ${optionsOpen ? "border-blue-400" : "border-gray-500"}`
                  : `bg-white hover:bg-gray-50 ${optionsOpen ? "border-blue-400" : "border-gray-300"}`
              }`}
            >
              表示設定
            </button>
          </div>
          {showNoDue && (
            <div
              className={`sticky top-0 z-30 p-2 border-b border-r font-semibold text-sm text-center relative ${isDarkMode ? "bg-gray-800 text-gray-200 border-gray-600" : "bg-gray-100"}`}
              style={{ left: `${LABEL_COL_WIDTH}px` }}
            >
              <div className="truncate">未設定</div>
              <div
                onMouseDown={(e) => handleColMouseDown(e, NO_DUE_DATE_COL_KEY)}
                className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300 opacity-50"
              />
            </div>
          )}
          {timelineDayKeys.map((key) => (
            <div
              key={key}
              className={`sticky top-0 z-20 p-2 border-b border-r font-semibold text-sm text-center relative ${
                key === todayKey
                  ? isDarkMode ? "bg-yellow-900 text-yellow-100 border-gray-600" : "bg-yellow-200"
                  : isDarkMode ? "bg-gray-800 text-gray-200 border-gray-600" : "bg-gray-50"
              }`}
            >
              <div className="truncate">{format(new Date(key), "M/d(E)")}</div>
              <div
                onMouseDown={(e) => handleColMouseDown(e, key)}
                className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300 opacity-50"
              />
            </div>
          ))}

          {/* 未割当行（ドラッグ可能） */}
          {showUnassigned && (
            <React.Fragment>
              <div className={`sticky left-0 z-20 p-2 font-semibold text-sm border-b border-r flex items-center pt-3 ${isDarkMode ? "bg-gray-800 text-gray-200 border-gray-600" : "bg-gray-100"}`}>
                未割当
              </div>
              <div
                className={`grid gap-y-1 py-1 pr-1 border-b border-r ${isDarkMode ? "border-gray-600" : ""}`}
                style={{
                  gridColumn: `span ${
                    showNoDue
                      ? timelineDayKeys.length + 1
                      : timelineDayKeys.length
                  } / span ${
                    showNoDue
                      ? timelineDayKeys.length + 1
                      : timelineDayKeys.length
                  }`,
                  gridTemplateColumns: `${
                    showNoDue ? getColWidth(NO_DUE_DATE_COL_KEY) + "px " : ""
                  }${timelineDayKeys
                    .map((key) => getColWidth(key) + "px")
                    .join(" ")}`,
                  gridTemplateRows: `repeat(${Math.max(
                    1,
                    timelineLayout.unassignedLayout.laneCount
                  )}, auto)`,
                  minHeight: "60px",
                }}
              >
                {showNoDue && (
                  <div
                    className={`sticky p-1 border-r h-full ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white"}`}
                    style={{ left: `${LABEL_COL_WIDTH}px`, zIndex: 25 }}
                  >
                    {timelineLayout.unassignedLayout.noDueDateTasks.map((t) => (
                      <TaskBar
                        key={t.id}
                        task={t}
                        showTitle={true}
                        color={color}
                        onShowDetail={setDetailTask}
                        roomStatuses={roomStatuses}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                )}
                {timelineLayout.unassignedLayout.tasks.map(
                  ({ task, lane, start, span, showTitle }) => (
                    <div
                      key={task.id}
                      style={{
                        gridRow: lane + 1,
                        gridColumn: `${
                          showNoDue ? start + 1 : start
                        } / span ${span}`,
                      }}
                    >
                      <TaskBar
                        task={task}
                        showTitle={showTitle}
                        color={color}
                        onShowDetail={setDetailTask}
                        roomStatuses={roomStatuses}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  )
                )}
              </div>
            </React.Fragment>
          )}

          {/* ユーザー行（ドラッグ可能） */}
          {orderedMembers
            .filter((member) => visibleUserIds.has(member.id))
            .map((member, index) => {
              const layout = timelineLayout.gridByUser[member.id];
              if (!layout) return null;
              return (
                <React.Fragment key={member.id}>
                  <div
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`sticky left-0 z-20 px-1 py-2 font-semibold text-sm border-b border-r flex items-center cursor-move ${
                      isDarkMode
                        ? `bg-gray-800 text-gray-200 border-gray-600 ${dragOverIndex === index ? "bg-gray-700" : ""}`
                        : `bg-blue-50 ${dragOverIndex === index ? "bg-blue-200" : ""}`
                    } ${draggedIndex === index ? "opacity-50" : ""}`}
                  >
                    <DragIndicatorIcon
                      fontSize="small"
                      className="text-gray-400 mr-1"
                    />
                    <span className="truncate">{member.name}</span>
                  </div>
                  <div
                    className={`grid gap-y-1 py-1 pr-1 border-b border-r ${isDarkMode ? "border-gray-600" : ""}`}
                    style={{
                      gridColumn: `span ${
                        showNoDue
                          ? timelineDayKeys.length + 1
                          : timelineDayKeys.length
                      } / span ${
                        showNoDue
                          ? timelineDayKeys.length + 1
                          : timelineDayKeys.length
                      }`,
                      gridTemplateColumns: `${
                        showNoDue
                          ? getColWidth(NO_DUE_DATE_COL_KEY) + "px "
                          : ""
                      }${timelineDayKeys
                        .map((key) => getColWidth(key) + "px")
                        .join(" ")}`,
                      gridTemplateRows: `repeat(${Math.max(
                        1,
                        layout.laneCount
                      )}, auto)`,
                      minHeight: "60px",
                    }}
                  >
                    {showNoDue && (
                      <div
                        className={`sticky p-1 border-r h-full ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white"}`}
                        style={{ left: `${LABEL_COL_WIDTH}px`, zIndex: 25 }}
                      >
                        {layout.noDueDateTasks.map((t) => (
                          <TaskBar
                            key={t.id}
                            task={t}
                            showTitle={true}
                            color={color}
                            onShowDetail={setDetailTask}
                            roomStatuses={roomStatuses}
                            isDarkMode={isDarkMode}
                          />
                        ))}
                      </div>
                    )}
                    {layout.tasks.map(
                      ({ task, lane, start, span, showTitle }) => (
                        <div
                          key={task.id}
                          style={{
                            gridRow: lane + 1,
                            gridColumn: `${
                              showNoDue ? start + 1 : start
                            } / span ${span}`,
                          }}
                        >
                          <TaskBar
                            task={task}
                            showTitle={showTitle}
                            color={color}
                            onShowDetail={setDetailTask}
                            roomStatuses={roomStatuses}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                      )
                    )}
                  </div>
                </React.Fragment>
              );
            })}
        </div>
      </div>

      {optionsOpen && (
        <div
          ref={optionsMenuRef}
          className={`absolute w-64 border rounded-md shadow-lg z-50 p-3 space-y-2 ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-200" : "bg-white"}`}
          style={{ top: optionsPosition.top, left: optionsPosition.left }}
        >
          {/* 未設定 */}
          <label className="flex items-center justify-between text-sm cursor-pointer select-none">
            <span>未設定(日付)</span>
            <Switch
              size="small"
              checked={showNoDue}
              onChange={(e) => setShowNoDue(e.target.checked)}
            />
          </label>

          {/* 区切り線 */}
          <div className={`border-t my-2 ${isDarkMode ? "border-gray-600" : "border-gray-300"}`} />

          {/* 未割当 */}
          <label className="flex items-center justify-between text-sm cursor-pointer select-none">
            <span>未割当</span>
            <Switch
              size="small"
              checked={showUnassigned}
              onChange={(e) => setShowUnassigned(e.target.checked)}
            />
          </label>

          {/* 各ユーザー */}
          {roomMembers.map((member) => (
            <label
              key={member.id}
              className="flex items-center justify-between text-sm cursor-pointer select-none"
            >
              <span className="truncate">{member.name}</span>
              <Switch
                size="small"
                checked={visibleUserIds.has(member.id)}
                onChange={(e) => {
                  const newSet = new Set(visibleUserIds);
                  if (e.target.checked) {
                    newSet.add(member.id);
                  } else {
                    newSet.delete(member.id);
                  }
                  setVisibleUserIds(newSet);
                }}
              />
            </label>
          ))}
        </div>
      )}

      {detailTask && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex justify-end"
          onClick={() => setDetailTask(null)}
        >
          <div
            className={`w-96 h-full shadow-lg animate-slide-in-right ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <TaskDetailPanel
              task={detailTask}
              isSubtask={(detailTask as any).isSubtask || false}
              parentForContext={
                (detailTask as any).isSubtask
                  ? tasks.find((t) => t.id === (detailTask as any).parentId) ??
                    detailTask
                  : detailTask
              }
              onUpdateTask={onUpdateTask}
              onUpdateStatus={handleUpdateStatus}
              onDeleteTask={onDeleteTask}
              roomMembers={roomMembers}
              availableTags={availableTags}
              onManageTags={onManageTags}
              color={color}
              addReminder={addReminder}
              deleteReminder={deleteReminder}
              onClose={() => setDetailTask(null)}
              displayMode="overlay"
              roomStatuses={roomStatuses}
              forceReload={forceReload}
              honbuId={honbuId}
              roomId={roomId}
              onOpenRecurrenceManagement={onOpenRecurrenceManagement}
            />
          </div>
        </div>
      )}
    </div>
  );
}
